// Global route helper that maps Laravel route names to Wayfinder functions
import * as routes from '../routes';
import * as adminRoutes from '../routes/admin';
import * as adminStaffRoutes from '../routes/admin/staff';
import * as adminDepartmentRoutes from '../routes/admin/departments';
import * as adminCollegeRoutes from '../routes/admin/colleges';
import * as adminSettingsRoutes from '../routes/admin/settings';
import * as adminUsersRoutes from '../routes/admin/users';
import * as staffRoutes from '../routes/staff';
import * as departmentRoutes from '../routes/departments';
import * as collegeRoutes from '../routes/colleges';
import * as loginRoutes from '../routes/login';
import * as registerRoutes from '../routes/register';
import * as passwordRoutes from '../routes/password';
import * as profileRoutes from '../routes/profile';
import * as verificationRoutes from '../routes/verification';
import * as storageRoutes from '../routes/storage';

// Route mapping object
const routeMap: Record<string, any> = {
  // Main routes
  'home': routes.home,
  'dashboard': routes.dashboard,
  'appearance': routes.appearance,
  'register': routes.register,
  'login': routes.login,
  'logout': routes.logout,
  
  // Staff routes
  'staff.index': staffRoutes.index,
  'staff.show': staffRoutes.show,
  'staff.auascv': staffRoutes.auascv,
  
  // Department routes
  'departments.index': departmentRoutes.index,
  'departments.show': departmentRoutes.show,

  // College routes
  'colleges.index': collegeRoutes.index,
  'colleges.show': collegeRoutes.show,
  
  // Admin routes
  'admin.dashboard': adminRoutes.dashboard,
  
  // Admin user routes
  'admin.users.index': adminUsersRoutes.index,
  'admin.users.store': adminUsersRoutes.store,
  'admin.users.update': adminUsersRoutes.update,
  'admin.users.destroy': adminUsersRoutes.destroy,
  
  // Admin settings routes
  'admin.settings.serpapi': adminSettingsRoutes.serpapi,
  'admin.settings.sync-scholar': adminSettingsRoutes.syncScholar,
  
  // Admin staff routes
  'admin.staff.index': adminStaffRoutes.index,
  'admin.staff.reorder': adminStaffRoutes.reorder,
  'admin.staff.reorder.post': adminStaffRoutes.reorder,
  'admin.staff.create': adminStaffRoutes.create,
  'admin.staff.store': adminStaffRoutes.store,
  'admin.staff.edit': adminStaffRoutes.edit,
  'admin.staff.update': adminStaffRoutes.update,
  'admin.staff.destroy': adminStaffRoutes.destroy,
  'admin.staff.toggle-status': adminStaffRoutes.toggleStatus,
  'admin.staff.move-to-top': adminStaffRoutes.moveToTop,
  'admin.staff.update-order': adminStaffRoutes.updateOrder,
  'admin.staff.bulk-action': adminStaffRoutes.bulkAction,
  'admin.staff.export': adminStaffRoutes.exportMethod,
  
  // Admin department routes
  'admin.departments.index': adminDepartmentRoutes.index,
  'admin.departments.create': adminDepartmentRoutes.create,
  'admin.departments.store': adminDepartmentRoutes.store,
  'admin.departments.edit': adminDepartmentRoutes.edit,
  'admin.departments.update': adminDepartmentRoutes.update,
  'admin.departments.destroy': adminDepartmentRoutes.destroy,
  'admin.departments.toggle-status': adminDepartmentRoutes.toggleStatus,
  'admin.departments.bulk-action': adminDepartmentRoutes.bulkAction,

  // Admin college routes
  'admin.colleges.index': adminCollegeRoutes.index,
  'admin.colleges.create': adminCollegeRoutes.create,
  'admin.colleges.store': adminCollegeRoutes.store,
  'admin.colleges.edit': adminCollegeRoutes.edit,
  'admin.colleges.update': adminCollegeRoutes.update,
  'admin.colleges.destroy': adminCollegeRoutes.destroy,
  'admin.colleges.toggle-status': adminCollegeRoutes.toggleStatus,
  'admin.colleges.bulk-action': adminCollegeRoutes.bulkAction,
  
  // Auth routes
  'login.store': loginRoutes.store,
  'register.store': registerRoutes.store,
  
  // Password routes
  'password.edit': passwordRoutes.edit,
  'password.update': passwordRoutes.update,
  'password.request': passwordRoutes.request,
  'password.email': passwordRoutes.email,
  'password.reset': passwordRoutes.reset,
  'password.store': passwordRoutes.store,
  'password.confirm': passwordRoutes.confirm,
  
  // Profile routes
  'profile.edit': profileRoutes.edit,
  'profile.update': profileRoutes.update,
  'profile.destroy': profileRoutes.destroy,
  
  // Verification routes
  'verification.notice': verificationRoutes.notice,
  'verification.verify': verificationRoutes.verify,
  'verification.send': verificationRoutes.send,
  
  // Storage routes
  'storage.local': storageRoutes.local,
};

/**
 * Global route helper function
 * @param name - Laravel route name
 * @param params - Route parameters (can be object, array, or single value)
 * @param options - Additional query options
 * @returns Route URL string
 */
export function route(name: string, params?: any, options?: any): string {
  const routeFunction = routeMap[name];
  
  if (!routeFunction) {
    console.warn(`Route '${name}' not found in route map`);
    return '#';
  }
  
  try {
    if (params === null || params === undefined) {
      const result = routeFunction(options);
      return typeof result === 'string' ? result : (result.url || '#');
    }
    
    const result = routeFunction(params, options);
    return typeof result === 'string' ? result : (result.url || '#');
  } catch (error) {
    console.error(`Error generating route '${name}':`, error);
    return '#';
  }
}

// Make route function available globally
declare global {
  const route: typeof import('./route').route;
  interface Window {
    route: typeof import('./route').route;
  }
}

// Attach to window and global scope for access
if (typeof window !== 'undefined') {
  (window as any).route = route;
  (globalThis as any).route = route;
}

// Export as default for easier importing
export default route;